import { forwardRef, useCallback, useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import QuadrantChart from '@aytm/app/stats/page/question-views/components/charts/highcharts/QuadrantChart'

import { centerQuadrantAxes } from '@aytm/app/stats/page/question-views/bpm/components/quadrant/components/chart/lib/quadrantAxisCentering'
import { updateChartLabels } from '@aytm/app/stats/page/question-views/bpm/components/quadrant/components/chart/lib/quadrantLabels'
import {
  calcModelData,
  expandScores,
  getBrandsFromChart,
  getChart,
  selectPoints,
  setPoints
} from '@aytm/app/stats/page/question-views/bpm/components/quadrant/components/chart/lib/utils'
import { buildOverlaySeries } from '@aytm/app/stats/page/question-views/bpm/components/quadrant/components/chart/lib/overlaySeries'

// The quadrant keeps the 400 its legacy view was laid out for, below the stats chart default; its
// placeholder holds the same height open, so nothing jumps when the chart arrives.
export const CHART_HEIGHT = 400

const BPMQuadrantChart = forwardRef(
  (
    {
      questionId,
      questionKey,
      questionVariants,
      batches,
      answer,
      scores,
      crossing,
      plots,
      title,
      dataLabels = 'hover',
      xAxisLimits,
      yAxisLimits,
      colorTheme,
      chartLegendMode,
      quadrantXAxisValue,
      quadrantYAxisValue,
      quadrantRadiusValue,
      quadrantColorValue,
      quadrantBrandsValue,
      features,
      exportDelegated = false,
      onBrandsChange,
      onChartReady
    },
    ref
  ) => {
    // Set from QuadrantChart's onChartReady api; getContainer() locates the highcharts DOM node.
    const chartApiRef = useRef(null)

    const resolvedScores = useMemo(() => (scores?.length ? scores : expandScores(answer)), [scores, answer])

    const modelData = useMemo(
      () =>
        calcModelData({
          questionVariants,
          batches,
          answer,
          quadrantXAxisValue,
          quadrantYAxisValue,
          quadrantRadiusValue,
          quadrantColorValue,
          colorTheme
        }),
      [
        answer,
        batches,
        questionVariants,
        quadrantXAxisValue,
        quadrantYAxisValue,
        quadrantRadiusValue,
        quadrantColorValue,
        colorTheme
      ]
    )

    // Sorted the same way QuadrantChart sorts before handing scores to the builder, so the
    // overlay's step and zMax fallback line up with the axes.
    const sortedScores = useMemo(() => [...resolvedScores].sort((a, b) => a - b), [resolvedScores])

    // Exactly one brand showing means a brand is selected; otherwise the value carries them all.
    const overlaySeries = useMemo(
      () =>
        buildOverlaySeries({
          answer,
          modelData,
          sortedScores,
          selectedId: quadrantBrandsValue?.length === 1 ? quadrantBrandsValue[0] : null,
          quadrantXAxisValue,
          quadrantYAxisValue
        }),
      [answer, modelData, sortedScores, quadrantBrandsValue, quadrantXAxisValue, quadrantYAxisValue]
    )

    const calcModelDataFn = useCallback(() => modelData, [modelData])

    const handleChartLoad = useCallback(
      function () {
        const chart = this
        centerQuadrantAxes(chart, modelData)
        if (dataLabels === 'on' || dataLabels === 'hover') {
          updateChartLabels(chart, chart.container, dataLabels)
        }
      },
      [modelData, dataLabels]
    )

    const handleChartRedraw = useCallback(
      function () {
        const chart = this
        centerQuadrantAxes(chart, modelData)
        if (dataLabels === 'on' || dataLabels === 'hover') {
          updateChartLabels(chart, chart.container, dataLabels)
        }
      },
      [modelData, dataLabels]
    )

    const handlePointClick = useCallback(
      event => {
        const chart = event.point.series.chart

        const brands = getBrandsFromChart(chart)
        onBrandsChange?.(brands)

        selectPoints(chart, event.point)
      },
      [answer, batches, quadrantXAxisValue, quadrantYAxisValue, onBrandsChange]
    )

    // Forward QuadrantChart's api (formatSVG for the pptx bridge) and keep it for chart lookup.
    const handleChartReady = useCallback(
      api => {
        chartApiRef.current = api
        onChartReady?.(api)
      },
      [onChartReady]
    )

    useEffect(() => {
      const chart = getChart(chartApiRef.current?.getContainer?.())
      if (!chart) return

      setPoints(chart, quadrantBrandsValue)
      selectPoints(chart)
    }, [
      answer,
      batches,
      quadrantXAxisValue,
      quadrantYAxisValue,
      quadrantColorValue,
      quadrantRadiusValue,
      quadrantBrandsValue,
      colorTheme
    ])

    return (
      <QuadrantChart
        ref={ref}
        questionId={questionId}
        questionKey={questionKey}
        calcModelData={calcModelDataFn}
        questionVariants={questionVariants}
        batches={batches}
        scores={resolvedScores}
        crossing={crossing}
        plots={plots}
        title={title}
        dataLabels={dataLabels}
        xAxisLimits={xAxisLimits}
        yAxisLimits={yAxisLimits}
        colorTheme={colorTheme}
        chartLegendMode={chartLegendMode}
        height={CHART_HEIGHT}
        pointClick={handlePointClick}
        chartLoad={handleChartLoad}
        chartRedraw={handleChartRedraw}
        overlaySeries={overlaySeries}
        features={features}
        exportDelegated={exportDelegated}
        onChartReady={handleChartReady}
      />
    )
  }
)

BPMQuadrantChart.propTypes = {
  questionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  questionKey: PropTypes.string,
  questionVariants: PropTypes.array.isRequired,
  batches: PropTypes.array.isRequired,
  answer: answerPropType,
  scores: PropTypes.arrayOf(PropTypes.number),
  crossing: PropTypes.array,
  plots: PropTypes.array,
  title: PropTypes.string,
  dataLabels: PropTypes.oneOf(['off', 'on', 'hover']),
  xAxisLimits: PropTypes.array,
  yAxisLimits: PropTypes.array,
  colorTheme: PropTypes.array,
  chartLegendMode: PropTypes.string,
  quadrantXAxisValue: PropTypes.string.isRequired,
  quadrantYAxisValue: PropTypes.string.isRequired,
  quadrantRadiusValue: PropTypes.string.isRequired,
  quadrantColorValue: PropTypes.string.isRequired,
  quadrantBrandsValue: PropTypes.arrayOf(PropTypes.string).isRequired,
  features: PropTypes.object,
  // Headless: a composite parent owns the export — parks ChartContainer's generic hook.
  exportDelegated: PropTypes.bool,
  onBrandsChange: PropTypes.func.isRequired,
  onChartReady: PropTypes.func
}

BPMQuadrantChart.displayName = 'BPMQuadrantChart'

export default BPMQuadrantChart
